import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useState } from "react";
import { API_BASE_URL } from "@/config/api.enpoints";
import { useScriptDashboardOverview, useScriptDetails, useUsers, useReassignScriptAssignment } from "@/hooks/useTenantDashboardService";
import useAuthStore from "@/store/useAuthStore";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";
import { toast } from "react-toastify";

function formatSectionTitle(typeStr: string, index: number): string {
  const clean = typeStr ? typeStr.replace(/_/g, " ") : `SECTION ${index + 1}`;
  return `${index + 1}. ${clean.toUpperCase()}`;
}

function formatDate(isoStr?: string): string {
  if (!isoStr) return "N/A";
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function getAudioFullUrl(url?: string | null): string | null {
  if (!url) return null;
  const trimmed = String(url).trim().replace(/\\/g, "/");
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  let cleanPath = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  cleanPath = cleanPath.replace(/^uploads\/uploads\//, "uploads/");
  if (cleanPath.startsWith("uploads/")) {
    return `${API_BASE_URL}/${cleanPath}`;
  }
  return `${API_BASE_URL}/uploads/${cleanPath}`;
}

const ScriptDetailsPage: React.FC = () => {
  const { isDark, colors } = useDashboardTheme();
  const accentColor = isDark ? "#B8F890" : "#5a9d50";
  const navigate = useNavigate();
  const location = useLocation();
  const { scriptId } = useParams<{ scriptId: string }>();

  const { tenantid, role } = useAuthStore();
  const tenantId = localStorage.getItem("tenantId") ?? tenantid;

  const [activeTab, setActiveTab] = useState<"learning" | "analysis">("analysis");
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({ 0: true, 1: true });

  const { data: overviewRes, isLoading: overviewLoading } = useScriptDashboardOverview(tenantId);
  const { data: singleScriptData, isLoading: detailsLoading } = useScriptDetails(scriptId || "");

  const isAdminOrManager = role === "SUPER_ADMIN" || role === "ADMIN" || role === "MANAGER";
  
  const { data: userData } = useUsers({
    currentPage: 1,
    pageSize: 100,
    search: "",
    role: "",
    currentAuthRole: role || "",
  });

  const { mutate: reassign, isPending: isReassigning } = useReassignScriptAssignment();
  
  const queryParams = new URLSearchParams(location.search);
  const assignedToIdParam = queryParams.get("assignedToId");

  const reps = userData?.data?.items || [];

  const handleBack = () => {
    const st = (location.state as any) || {};
    const returnState = {
      page: st.page || "dashboard",
      selectedRep: st.selectedRep || st.rep,
      repTab: st.repTab || st.tab || "scripts",
    };
    const candidate = st?.from || st?.returnTo || st?.fromPath || st?.prevPath || st?.backPath;
    if (candidate) {
      try {
        if (typeof candidate === 'string') {
          navigate(candidate, { state: returnState });
          return;
        }
        if (typeof candidate === 'object' && candidate.pathname) {
          navigate(candidate.pathname, { state: { ...(candidate.state || {}), ...returnState } });
          return;
        }
      } catch (e) {
        // ignore
      }
    }
    if (window.history && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/meeting-dashboard', { state: returnState });
    }
  };

  const overviewActivities = overviewRes?.data?.recentPracticeActivities || [];
  const overviewScript = overviewActivities.find(
    (act: any) => act.assignmentId === scriptId || act.scriptMetaId === scriptId
  );

  const currentAssignment = singleScriptData?.scriptMeta?.ScriptGenAssignment?.find(
    (a: any) =>
      (assignedToIdParam && a.assignedToId === assignedToIdParam) ||
      (overviewScript?.assignmentId && a.id === overviewScript.assignmentId)
  ) || singleScriptData?.scriptMeta?.ScriptGenAssignment?.[0];

  const assignmentId = currentAssignment?.id || overviewScript?.assignmentId;

  const title = overviewScript?.scriptTitle || singleScriptData?.scriptMeta?.title || "Script Details";
  const scriptType = (
    overviewScript?.scriptType ||
    singleScriptData?.scriptMeta?.scripttype ||
    "Call"
  ).toUpperCase();

  const assignedToName = overviewScript?.repName || "Bhagyashree Rajput user";
  const assignedByName = overviewScript?.assignedBy || "superlatest";
  const assignedAtDate = formatDate(overviewScript?.createdAt || singleScriptData?.scriptMeta?.createdAt);

  const rawSections = overviewScript?.sections || singleScriptData?.sections || [];
  const completedSectionsCount = rawSections.filter((s: any) => s.isCompleted).length;
  const totalSectionsCount = rawSections.length || 4;

  const toggleSection = (idx: number) => {
    setOpenSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const isLoading = overviewLoading && detailsLoading;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colors.bg,
        color: colors.text,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "24px 32px",
      }}
    >
      {/* Top Navigation Back Button */}
      <button
        onClick={handleBack}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: colors.surfaceAlt,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: "8px 16px",
          color: colors.text,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          marginBottom: 20,
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)";
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {isLoading ? (
        <div style={{ padding: 60, textAlign: "center", color: colors.textMuted }}>
          Loading script details...
        </div>
      ) : (
        <>
          {/* Script Card Header Block */}
          <div
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 16,
              padding: "24px 28px",
              marginBottom: 28,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <h1
                    style={{
                      fontSize: 26,
                      fontWeight: 800,
                      color: colors.text,
                      margin: 0,
                    }}
                  >
                    {title}
                  </h1>

                  {/* Type Badge */}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: isDark ? "rgba(74, 222, 128, 0.12)" : "rgba(34, 197, 94, 0.12)",
                      border: `1px solid ${isDark ? "rgba(74, 222, 128, 0.3)" : "rgba(34, 197, 94, 0.25)"}`,
                      color: isDark ? "#4ADE80" : "#15803D",
                      borderRadius: 20,
                      padding: "4px 14px",
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: "capitalize",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: isDark ? "#4ADE80" : "#15803D",
                      }}
                    />
                    {scriptType}
                  </span>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    fontSize: 14,
                    color: colors.textMuted,
                    marginTop: 12,
                  }}
                >
                  <div>
                    <span style={{ color: colors.textMuted, fontWeight: 500 }}>Assigned By: </span>
                    <span style={{ color: colors.text, fontWeight: 700 }}>{assignedByName}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ color: colors.textMuted, fontWeight: 500 }}>Assigned To: </span>
                    {isAdminOrManager && assignmentId ? (
                      <select
                        value={currentAssignment?.assignedToId || ""}
                        onChange={async (e) => {
                          const newUserId = e.target.value;
                          if (!newUserId || newUserId === currentAssignment?.assignedToId) return;
                          toast(
                            ({ closeToast }) => (
                              <div className="flex flex-col gap-3">
                                <p className="text-sm font-medium">Are you sure you want to reassign this script? Previous progress will be reset.</p>
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={closeToast as any}
                                    style={{ padding: "4px 12px", background: "transparent", border: "none", color: "#ccc", cursor: "pointer" }}
                                  >
                                    CANCEL
                                  </button>
                                  <button
                                    onClick={() => {
                                      (closeToast as any)();
                                      reassign(
                                        { assignmentId, assignedToId: newUserId },
                                        {
                                          onSuccess: () => {
                                            const newRep = reps.find((r: any) => r.id === newUserId);
                                            toast.success(`Script successfully reassigned to ${newRep?.name || "new representative"}`);
                                            navigate(`/script-details/${scriptId}?assignedToId=${newUserId}`, { replace: true });
                                          },
                                          onError: (err: any) => {
                                            toast.error(`Failed to reassign script: ${err.message || err}`);
                                          },
                                        }
                                      );
                                    }}
                                    style={{ padding: "4px 12px", background: "rgba(244, 63, 94, 0.2)", borderRadius: 6, border: "none", color: "#f43f5e", cursor: "pointer", fontWeight: "bold" }}
                                  >
                                    REASSIGN
                                  </button>
                                </div>
                              </div>
                            ),
                            { autoClose: false, closeOnClick: false, draggable: false }
                          );
                        }}
                        disabled={isReassigning}
                        style={{
                          background: colors.surfaceAlt,
                          border: `1px solid ${colors.border}`,
                          borderRadius: 8,
                          padding: "2px 8px",
                          color: "#ffffff",
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: "pointer",
                          outline: "none",
                          marginLeft: 6,
                        }}
                      >
                        {reps.map((repUser: any) => (
                          <option
                            key={repUser.id}
                            value={repUser.id}
                            style={{ background: colors.surface, color: "#ffffff" }}
                          >
                            {repUser.name || repUser.email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: colors.text, fontWeight: 700, marginLeft: 6 }}>{assignedToName}</span>
                    )}
                    {((currentAssignment as any)?.reassignCount || 0) > 0 && (
                      <span style={{ color: colors.textMuted, fontSize: 13, marginLeft: 8 }}>
                        (Reassigned {(currentAssignment as any).reassignCount} {(currentAssignment as any).reassignCount === 1 ? 'time' : 'times'})
                      </span>
                    )}
                  </div>
                  <div>
                    <span style={{ color: colors.textMuted, fontWeight: 500 }}>Assigned At: </span>
                    <span style={{ color: colors.text, fontWeight: 600 }}>{assignedAtDate}</span>
                    <span style={{ color: colors.textMuted, fontWeight: 500, marginLeft: 12 }}>Attempts: </span>
                    <span style={{ color: colors.text, fontWeight: 600 }}>{(currentAssignment as any)?.attemptCount || 0}</span>
                  </div>
                </div>
              </div>

              {/* Right Action & Progress */}
              <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                {/* Progress Ring / Gauge */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 14, color: colors.textMuted, fontWeight: 600 }}>
                      Progress: {completedSectionsCount}/{totalSectionsCount}
                    </div>
                  </div>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      border: `3px solid ${accentColor}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 800,
                      color: accentColor,
                    }}
                  >
                    {Math.round((completedSectionsCount / totalSectionsCount) * 100)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Navigation Tabs */}
          <div
            style={{
              display: "flex",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <button
              onClick={() => setActiveTab("learning")}
              style={{
                background: activeTab === "learning" ? accentColor : colors.surfaceAlt,
                color: activeTab === "learning" ? (isDark ? "#0d0d1a" : "#ffffff") : colors.textMuted,
                border: activeTab === "learning" ? `1px solid ${accentColor}` : `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: "10px 22px",
                fontWeight: activeTab === "learning" ? 700 : 600,
                fontSize: 14,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Learning
            </button>
            <button
              onClick={() => setActiveTab("analysis")}
              style={{
                background: activeTab === "analysis" ? accentColor : colors.surfaceAlt,
                color: activeTab === "analysis" ? (isDark ? "#0d0d1a" : "#ffffff") : colors.textMuted,
                border: activeTab === "analysis" ? `1px solid ${accentColor}` : `1px solid ${colors.border}`,
                borderRadius: 10,
                padding: "10px 22px",
                fontWeight: activeTab === "analysis" ? 700 : 600,
                fontSize: 14,
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Pitchster Analysis
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "learning" ? (
            <div
              style={{
                background: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: 16,
                padding: 24,
              }}
            >
              <h3 style={{ marginTop: 0, color: colors.text, fontSize: 18 }}>Script Overview & Learning Material</h3>
              {rawSections.map((sec: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: 20,
                    padding: 16,
                    background: colors.surfaceAlt,
                    borderRadius: 12,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <h4 style={{ margin: "0 0 8px 0", color: accentColor }}>
                    {formatSectionTitle(sec.type, idx)}
                  </h4>
                  <p style={{ color: colors.text, lineHeight: 1.6, margin: 0 }}>
                    {sec.content || "No content specified for this section."}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {rawSections.map((section: any, idx: number) => {
                const isOpen = !!openSections[idx];
                const score = section.score || {};
                const audioUrl = getAudioFullUrl(section.audioUrl);
                const overallPct = section.overallScore ?? 0;

                return (
                  <div
                    key={idx}
                    style={{
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 14,
                      overflow: "hidden",
                    }}
                  >
                    {/* Section Header Accordion */}
                    <div
                      onClick={() => toggleSection(idx)}
                      style={{
                        padding: "20px 26px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        background: isOpen ? colors.surfaceAlt : "transparent",
                        userSelect: "none",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontWeight: 700, fontSize: 16, color: colors.text }}>
                          {formatSectionTitle(section.type, idx)}
                        </span>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span
                          style={{
                            background: section.isCompleted
                              ? isDark ? "rgba(74, 222, 128, 0.15)" : "rgba(34, 197, 94, 0.12)"
                              : isDark ? "rgba(250, 204, 21, 0.15)" : "rgba(234, 179, 8, 0.12)",
                            color: section.isCompleted
                              ? isDark ? "#4ADE80" : "#15803D"
                              : isDark ? "#FACC15" : "#A16207",
                            border: `1px solid ${
                              section.isCompleted
                                ? isDark ? "rgba(74, 222, 128, 0.3)" : "rgba(34, 197, 94, 0.25)"
                                : isDark ? "rgba(250, 204, 21, 0.3)" : "rgba(234, 179, 8, 0.25)"
                            }`,
                            borderRadius: 16,
                            padding: "4px 14px",
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {section.isCompleted ? "Completed" : "In Progress"}
                        </span>
                        <span style={{ color: colors.textMuted, fontSize: 15 }}>
                          {isOpen ? "▲" : "▼"}
                        </span>
                      </div>
                    </div>

                    {/* Section Content */}
                    {isOpen && (
                      <div style={{ padding: "22px 26px", borderTop: `1px solid ${colors.border}` }}>
                        <p style={{ color: colors.text, marginBottom: 24, fontSize: 15, lineHeight: 1.7 }}>
                          {section.content}
                        </p>

                        {/* Performance Analysis Box */}
                        <div
                          style={{
                            background: colors.surfaceAlt,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 12,
                            padding: 22,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: 20,
                            }}
                          >
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: colors.text }}>
                              Performance Analysis
                            </h3>
                            <span
                              style={{
                                background: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
                                border: `1px solid ${colors.border}`,
                                borderRadius: 16,
                                padding: "5px 14px",
                                fontSize: 13,
                                fontWeight: 700,
                                color: colors.text,
                              }}
                            >
                              Overall: {overallPct}%
                            </span>
                          </div>

                          {/* 5 Score Breakdown Parameters */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                              gap: 16,
                              marginBottom: 22,
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                                Clarity
                              </div>
                              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>
                                {score.clarity ?? 0}/5
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                                Structure
                              </div>
                              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>
                                {score.structure ?? 0}/5
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                                Confidence
                              </div>
                              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>
                                {score.confidence ?? 0}/5
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                                Engagement
                              </div>
                              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>
                                {score.engagement ?? 0}/5
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4, fontWeight: 500 }}>
                                Key Phrases
                              </div>
                              <div style={{ fontSize: 17, fontWeight: 700, color: colors.text }}>
                                {score.keyPhrases ?? 0}/5
                              </div>
                            </div>
                          </div>

                          {/* Feedback Text */}
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, marginBottom: 8 }}>
                              Feedback:
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                color: colors.text,
                                lineHeight: 1.7,
                                background: colors.surface,
                                padding: 14,
                                borderRadius: 10,
                                border: `1px solid ${colors.border}`,
                              }}
                            >
                              {section.feedbackSummary ||
                                "No feedback recorded yet. Submit your recording to get instant AI coaching recommendations."}
                            </div>
                          </div>

                          {/* Audio Player */}
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, marginBottom: 8 }}>
                              Your Recording:
                            </div>
                            {audioUrl ? (
                              <audio
                                controls
                                src={audioUrl}
                                style={{
                                  width: "100%",
                                  maxHeight: 40,
                                  borderRadius: 8,
                                  outline: "none",
                                }}
                              />
                            ) : (
                              <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: "italic" }}>
                                No audio recording available for this section.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScriptDetailsPage;
